import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { adminListDisputes, adminResolveDispute } from "@/lib/bookings.functions";
import { Loader2, ShieldAlert } from "lucide-react";
import { formatGBP } from "@/lib/pricing";

export const Route = createFileRoute("/_authenticated/admin/disputes")({
  head: () => ({ meta: [{ title: "Disputes — Admin — Peopels" }, { name: "robots", content: "noindex" }] }),
  component: AdminDisputes,
});

type Dispute = {
  id: string;
  status: string;
  reason: string;
  description: string | null;
  refund_amount: number | null;
  resolution: string | null;
  created_at: string;
  booking: {
    id: string;
    total_amount: number;
    helper_payout: number;
    payment_intent_id: string | null;
    customer: { id: string; full_name: string | null; email: string | null } | null;
    helper: { id: string; full_name: string | null; email: string | null } | null;
  } | null;
};

function AdminDisputes() {
  const navigate = useNavigate();
  const list = useServerFn(adminListDisputes);
  const [loading, setLoading] = useState(true);
  const [disputes, setDisputes] = useState<Dispute[]>([]);

  useEffect(() => {
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) { navigate({ to: "/auth", search: { redirect: "/admin/disputes" } }); return; }
      try {
        const res = await list();
        setDisputes(res.disputes as unknown as Dispute[]);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not load disputes");
      } finally { setLoading(false); }
    })();
  }, [list, navigate]);

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-5xl flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-primary" />
          <h1 className="font-display text-3xl font-bold">Disputes</h1>
        </div>
        {loading ? (
          <div className="mt-12 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : disputes.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground">No disputes raised. 🎉</div>
        ) : (
          <ul className="mt-8 space-y-4">
            {disputes.map((d) => <DisputeRow key={d.id} d={d} onResolved={() => setDisputes((arr) => arr.filter((x) => x.id !== d.id))} />)}
          </ul>
        )}
      </main>
      <Footer />
    </div>
  );
}

function DisputeRow({ d, onResolved }: { d: Dispute; onResolved: () => void }) {
  const resolve = useServerFn(adminResolveDispute);
  const [resolution, setResolution] = useState<"refund_customer" | "pay_helper" | "split">("refund_customer");
  const [refundAmount, setRefundAmount] = useState(String(d.booking?.total_amount ?? 0));
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onResolve() {
    setSubmitting(true);
    try {
      await resolve({ data: { disputeId: d.id, resolution, refundAmount: resolution === "pay_helper" ? undefined : Number(refundAmount), notes: notes || undefined } });
      toast.success("Dispute resolved");
      onResolved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not resolve");
    } finally { setSubmitting(false); }
  }

  return (
    <li className="rounded-2xl border border-border bg-card p-6 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-lg font-semibold">{d.reason}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Raised {new Date(d.created_at).toLocaleString("en-GB")} ·
            Customer: {d.booking?.customer?.full_name ?? d.booking?.customer?.email ?? "—"} ·
            Helper: {d.booking?.helper?.full_name ?? d.booking?.helper?.email ?? "—"}
          </p>
          {d.description && <p className="mt-3 text-sm text-foreground">{d.description}</p>}
          <p className="mt-3 text-xs text-muted-foreground">Booking total {formatGBP(Number(d.booking?.total_amount ?? 0))} · Helper payout {formatGBP(Number(d.booking?.helper_payout ?? 0))}</p>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <div>
          <Label>Resolution</Label>
          <select value={resolution} onChange={(e) => setResolution(e.target.value as typeof resolution)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
            <option value="refund_customer">Refund customer</option>
            <option value="pay_helper">Pay helper in full</option>
            <option value="split">Split (partial refund)</option>
          </select>
        </div>
        {resolution !== "pay_helper" && (
          <div>
            <Label>Refund amount (£)</Label>
            <Input type="number" min={0} step="0.01" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} className="mt-1" />
          </div>
        )}
        <div className="sm:col-span-3">
          <Label>Notes</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" />
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={onResolve} disabled={submitting}>{submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Resolving…</> : "Resolve dispute"}</Button>
      </div>
    </li>
  );
}
